import { MeteringMemberUsage } from '@@types/Api';
import { CHANNEL_ID } from '@@types/common';
import { MeteringUsage } from '@@types/Metering';

import { ALERT_METERING_TYPE } from '~/constants';
import { MINUTE } from '~/constants/date';
import localStorage from '~/services/localStorage';
import Actions from '~/stores/Actions';
import { MeteringBannerType, meteringStore } from '~/stores/MeteringStore';
import { api } from '~/stores/providers/Api2';
import { stores } from '~/stores/Stores';
import { userStore } from '~/stores/UserStore';
import { isAdminOfAnyType } from '~/utils/adminOrganizationUtil';
import { isEmpty } from 'lodash';
import { action, comparer, reaction } from 'mobx';
import { complement, find, ifElse, mergeRight, pick, pipe, propEq, propOr, when } from 'ramda';

const METERING_ALERT_KEY = 'MESSENGER_METERING_ALERT';

class MeteringActions {
  myUsage: any = {
    sended: 0,
    data: {},
  };

  messengerUsage: any = {
    sended: 0,
    data: {},
  };

  constructor(private actions: Actions) {
    this.init();
  }

  init = () => {
    this.loadStorage();
    reaction(() => meteringStore.hideAlert, this.saveStorage, { equals: comparer.shallow });
  };

  loadStorage = () => {
    when(complement(isEmpty), meteringStore.setHideAlert)(localStorage.get(METERING_ALERT_KEY));
  };

  saveStorage = () => {
    localStorage.set(METERING_ALERT_KEY)(meteringStore.hideAlert);
  };

  fetchAndSet = async (channelId: CHANNEL_ID) => {
    const usage: MeteringUsage = await this.fetchUsageData(channelId);
    meteringStore.setUsage(channelId, usage);
  };

  @action
  setChannelBanner = (channelId: CHANNEL_ID) => {
    const usage = meteringStore.getUsage(channelId);
    let bannerType = this.getShowBannerType(usage);
    const isMeChannel = this.actions.channelActions.isMeChannelId(channelId);
    if (meteringStore.isSetHide(isMeChannel, bannerType)) {
      bannerType = null;
    }

    if (isMeChannel || (stores.user && isAdminOfAnyType(stores.user))) {
      meteringStore.setShowBannerType(bannerType);
    }
    this.resetHideAlert(isMeChannel, usage);
  };

  private resetHideAlert = (isMeChannel: boolean, usage: MeteringUsage) => {
    if (!usage.almostOver) {
      isMeChannel
        ? meteringStore.setHideAlert({ meIsOver: false, meAlmostOver: false })
        : meteringStore.setHideAlert({ tenantIsOver: false, tenantAlmostOver: false });
      return;
    }

    if (!usage.over) {
      isMeChannel
        ? meteringStore.setHideAlert({ meIsOver: false })
        : meteringStore.setHideAlert({ tenantIsOver: false });
    }
  };

  private getShowBannerType = (usage: MeteringUsage): MeteringBannerType => {
    return usage.over ? ALERT_METERING_TYPE.IS_OVER : usage.almostOver ? ALERT_METERING_TYPE.ALMOST_OVER : null;
  };

  private fetchUsageData = (channelId): Promise<MeteringUsage> => {
    return ifElse(this.actions.channelActions.isMeChannelId, this.fetchMyUsage, this.fetchMessengerUsage)(channelId);
  };

  private fetchMyUsage = async (): Promise<MeteringUsage> => {
    if (this.myUsage.sended > Date.now() - 5 * MINUTE) {
      return this.myUsage.data;
    }
    try {
      const myUsage = await api.common.fetchMyUsage(userStore.id);
      const data = mergeRight(pick(['over', 'almostOver', 'limitBytes', 'usageBytes'], myUsage), {
        messengerBytes: this.findMessengerUsage(myUsage),
      });

      this.myUsage = {
        sended: Date.now(),
        data,
      };
      return data;
    } catch (e) {
      this.myUsage.sended = Date.now();
      return this.myUsage.data;
    }
  };

  private fetchMessengerUsage = async (): Promise<MeteringUsage> => {
    if (this.messengerUsage.sended > Date.now() - 5 * MINUTE) {
      return this.messengerUsage.data;
    }
    try {
      const messengerUsage = await api.common.fetchMessengerUsage();

      const data = {
        messengerBytes: messengerUsage.usageBytes,
        ...messengerUsage.tenant,
      };
      this.messengerUsage = {
        sended: Date.now(),
        data,
      };
      return data;
    } catch (e) {
      this.messengerUsage.sended = Date.now();
      return this.messengerUsage.data;
    }
  };

  private findMessengerUsage = (usage: MeteringMemberUsage) => {
    if (!usage.services) {
      return '0';
    }
    return pipe(find(propEq('code', 'messenger')), propOr('0', 'usageBytes'))(usage.services) as string;
  };
}

export default MeteringActions;
